import { pageMetadata, syncMetadata } from "../lib/seo";
import { isSponsoredStory } from "../lib/sponsorship";
import { publicationDateLabel } from "../lib/publicationTime";
import { EDITOR_BYLINE, fetchPostBySlug, splitArticleBody, fetchWordPressAuthor, mapAuthor } from "../lib/wordpress";
import { mergeAuthorPosts } from "../data/stories";
import NewsSidebar from "./NewsSidebar";
import { titleCaseHeadline } from "../utils/titleCase";
import AdSlot from "./AdSlot";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Link } from "./CanonicalLink";
import {
  Clock,
  Facebook,
  Twitter,
  Link2,
  Check,
  Home,
  Bookmark,
  Heart,
} from "lucide-react";
import { motion } from "framer-motion";
import TopBar from "./TopBar";
import Masthead from "./Masthead";
import NewsletterBand from "./NewsletterBand";
import Footer from "./Footer";
import ReadingProgress from "./ReadingProgress";
import Reveal from "./Reveal";
import {
  SITE,
  STORIES,
  getStoryById,
  getStoryCategory,
  CATEGORY_LABELS,
  authorSlug,
} from "../data/stories";
import { getArticle, getRelated } from "../data/articles";
import { initialsOf } from "./AuthorPage";
import { trackView, fetchApplause, sendApplause } from "../api";
import { isSaved, toggleSavedStory } from "../utils/saved";

const word = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.05, ease: [0.19, 1, 0.22, 1] },
  }),
};

/* Word-level masked reveal: wraps naturally at any width, never forces breaks */
const MaskedWords = ({ text, testid, className }) => {
  const words = text.split(" ");
  return (
    <h1 data-testid={testid} aria-label={text} className={className}>
      {words.map((w, i) => (
        <span key={i}>
          <span
            aria-hidden="true"
            className="inline-block overflow-hidden pb-[0.14em] -mb-[0.14em] align-bottom"
          >
            <motion.span
              className="inline-block"
              variants={word}
              initial="hidden"
              animate="show"
              custom={i}
            >
              {w}
            </motion.span>
          </span>
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </h1>
  );
};

const ShareRail = ({ story, vertical = false, bookmarkOnly = false }) => {
  const [copied, setCopied] = useState(false);
  const [savedNow, setSavedNow] = useState(false);
  useEffect(() => { setSavedNow(isSaved(story.id)); }, [story.id]);
  const url = `https://standard.gm/${story.id}/`;

  const toggleSave = () => {
    const next = toggleSavedStory(story.id);
    setSavedNow(next.includes(story.id));
  };

  const shares = [
    {
      id: "facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      label: "Share on Facebook",
    },
    {
      id: "x",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(story.title)}&url=${encodeURIComponent(url)}`,
      label: "Share on X",
    },
    {
      id: "whatsapp",
      icon: null,
      href: `https://wa.me/?text=${encodeURIComponent(story.title + " " + url)}`,
      label: "Share on WhatsApp",
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const btnCls =
    "w-10 h-10 rounded-[8px] border border-[var(--line)] flex items-center justify-center text-[var(--muted)] hover:bg-[#cf0a0a] hover:border-[#cf0a0a] hover:text-white transition-colors duration-300";

  return (
    <div
      data-testid="article-share"
      className={`flex ${vertical ? "flex-col sticky top-24" : "flex-row"} gap-2.5`}
    >
      {bookmarkOnly && <button
        onClick={toggleSave}
        aria-label={savedNow ? "Remove from saved stories" : "Save story"}
        title={savedNow ? "Remove from saved stories" : "Save story"}
        data-testid="save-story-button"
        className={`h-10 w-10 flex items-center justify-center ${savedNow ? "text-[#cf0a0a]" : "text-[var(--ink)]"} hover:text-[#cf0a0a] cursor-pointer transition-colors`}
      >
        <Bookmark size={24} fill={savedNow ? "currentColor" : "none"} />
      </button>}
      {!bookmarkOnly && shares.map(({ id, icon: Icon, href, label }) => (
        <a
          key={id}
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={label}
          data-testid={`share-${id}`}
          className={btnCls}
        >
          {id === "whatsapp" ? (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M12 2a10 10 0 0 0-8.66 15.01L2 22l5.13-1.31A10 10 0 1 0 12 2Zm5.46 14.1c-.23.65-1.35 1.24-1.86 1.29-.5.05-.97.24-3.27-.68-2.77-1.09-4.53-3.93-4.67-4.11-.13-.18-1.11-1.48-1.11-2.82 0-1.34.7-2 .95-2.27.25-.28.55-.35.73-.35h.53c.17 0 .4-.07.63.48.23.55.8 1.94.86 2.08.07.14.11.3.02.48-.09.18-.5.73-.7.97-.13.16-.28.3-.12.58.16.28.72 1.19 1.55 1.93 1.06.95 1.96 1.24 2.24 1.38.28.14.44.12.61-.07.16-.19.7-.82.89-1.1.19-.28.37-.23.63-.14.25.09 1.62.76 1.9.9.28.14.46.21.53.33.07.11.07.66-.16 1.3Z" />
            </svg>
          ) : (
            <Icon size={14} />
          )}
        </a>
      ))}
      {!bookmarkOnly && <button
        onClick={copyLink}
        aria-label="Copy link"
        data-testid="share-copy"
        className={`${btnCls} ${copied ? "bg-[#141414] border-[#141414] text-white" : ""}`}
      >
        {copied ? <Check size={14} /> : <Link2 size={14} />}
      </button>}
      {copied && (
        <span
          data-testid="share-copied-feedback"
          className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--accent)] self-center"
        >
          Link copied
        </span>
      )}
    </div>
  );
};

const RelatedCard = ({ story }) => (
  <Link
    to={`/${story.id}`}
    data-testid={`related-story-${story.id}`}
    className="group block py-4"
  >
    <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--accent)]">
      {story.kicker || "The Standard"}
    </p>
    <h3 className="mt-1.5 font-display text-lg leading-snug font-medium group-hover:text-[var(--accent)] transition-colors duration-300">
      {titleCaseHeadline(story.title)}
    </h3>
    <p className="mt-1.5 text-[11px] text-[var(--faint)]">{story.date}</p>
  </Link>
);

export const ArticlePage = () => {
  const { id } = useParams();
  const [, refresh] = useState(0);
  const [articleLoading, setArticleLoading] = useState(!getStoryById(id)?.hasFullContent);
  const story = getStoryById(id);
  useEffect(() => {
    let active = true;
    if (getStoryById(id)?.hasFullContent) { setArticleLoading(false); return; }
    setArticleLoading(true);
    fetchPostBySlug(id).then(post => { if(active && post) { mergeAuthorPosts([post]); refresh(n => n + 1); } }).catch(() => {}).finally(() => {if(active)setArticleLoading(false);});
    return () => { active = false; };
  }, [id]);
  const [applause, setApplause] = useState(null);
  const [applauded, setApplauded] = useState(false);

  useEffect(() => {
    if(story) syncMetadata(pageMetadata({pathname:window.location.pathname,story}));
  }, [story]);

  useEffect(() => {
    if (!story) return;
    const seenKey = `std-viewed-${story.id}`;
    if (!sessionStorage.getItem(seenKey)) {
      sessionStorage.setItem(seenKey, "1");
      trackView(story.id);
    }
  }, [story]);

  useEffect(() => {
    if (!story) return undefined;
    let live = true;
    setApplause(null);
    setApplauded(sessionStorage.getItem(`std-applauded-${story.id}`) === "1");
    fetchApplause(story.id).then((n) => {
      if (live && n != null) setApplause(n);
    });
    return () => {
      live = false;
    };
  }, [story]);

  const applaud = async () => {
    if (applauded) return;
    setApplauded(true);
    sessionStorage.setItem(`std-applauded-${story.id}`, "1");
    const n = await sendApplause(story.id);
    if (n != null) setApplause(n);
  };

  const [authorRec, setAuthorRec] = useState(story?.authorRecord || null);

  useEffect(() => {
    if (!story) return undefined;
    let live = true;
    setAuthorRec(story.authorRecord || null);
    fetchWordPressAuthor(String(story.authorRecord?.id || authorSlug(story.author || EDITOR_BYLINE))).then((record) => {
      const a = record ? mapAuthor(record) : null;
      if (live && a) setAuthorRec(a);
    }).catch(() => {});
    return () => {
      live = false;
    };
  }, [story]);

  if (articleLoading && !story) return <><TopBar/><Masthead/><main className="py-24 text-center">Loading story…</main><Footer/></>;
  if (!story) {
    return (
      <div data-testid="article-not-found">
        <TopBar />
        <Masthead />
        <div className="mx-auto max-w-[1440px] px-6 py-24 text-center">
          <h1 className="font-display text-4xl">Story not found</h1>
          <p className="mt-3 text-[var(--muted)]">
            The story you are looking for is not on the front page today.
          </p>
          <Link
            to="/"
            data-testid="not-found-home-link"
            className="mt-6 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[var(--accent)]"
          >
            <Home size={14} /> Back to the front page
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const { paragraphs, readingTime } = getArticle(story);
  const related = getRelated(story.id, story.kicker, 4);
  const category = getStoryCategory(story.id);
  const categoryLabel = category ? CATEGORY_LABELS[category] || category : null;
  const otherNews = STORIES.filter(item => item.id !== story.id).slice(0, 9);
  const topStories = STORIES.filter(item => item.id !== story.id).slice(0, 10);
  const bodyParts = story.bodyHtml ? splitArticleBody(story.bodyHtml) : null;

  return (
    <div key={story.id} data-testid="article-page">
      <ReadingProgress />
      <TopBar />
      <Masthead />

      <main>
        <article className="mx-auto max-w-[1440px] px-4 sm:px-6 pt-10 sm:pt-14 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="hidden lg:block lg:col-span-1"><ShareRail story={story} vertical /></div>
            <div className="lg:col-span-8">
              {isSponsoredStory(story) && (
                <aside data-testid="sponsored-disclosure" aria-label="Sponsored content disclosure"
                  className="mb-6 rounded-[8px] border border-[var(--line)] bg-[var(--bg-soft)] px-4 py-3 sm:px-5">
                  <p className="text-[12px] font-bold uppercase tracking-[0.12em] text-[#cf0a0a] dark:text-[#ff7373]">Sponsored Content</p>
                  <p className="mt-1 text-[13px] leading-6 text-[var(--muted)]">This article is paid promotional content.</p>
                </aside>
              )}
              <MaskedWords
                text={titleCaseHeadline(story.title)}
                testid="article-title"
                className="-ml-4 sm:ml-0 border-l-[4px] sm:border-l-[10px] border-[#cf0a0a] pl-4 sm:pl-6 font-display font-semibold text-4xl sm:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-[-0.01em]"
              />

              <div
                data-testid="article-meta"
                className="mt-5 flex items-center gap-3 sm:flex-wrap sm:gap-x-4 sm:gap-y-3 text-[12px] text-[var(--muted)]"
              >
                <span className="flex min-w-0 items-center gap-2.5">
                  {authorRec && authorRec.has_avatar ? (
                    <img
                      src={authorRec.avatar}
                      onError={() => setAuthorRec(current => current ? { ...current, has_avatar: false } : current)}
                      alt=""
                      data-testid="article-author-avatar"
                      className="w-9 h-9 rounded-full object-cover ring-1 ring-black/10 shrink-0"
                    />
                  ) : (
                    <span
                      data-testid="article-author-avatar"
                      aria-hidden="true"
                      className="w-9 h-9 rounded-full bg-[#cf0a0a] text-white flex items-center justify-center text-[11px] font-extrabold tracking-wide shrink-0"
                    >
                      {initialsOf(story.authorRecord?.name || story.author || EDITOR_BYLINE)}
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="block sm:inline">By{" "}
                    {(story.authors?.length ? story.authors : [story.authorRecord || {name:story.author || EDITOR_BYLINE}]).map((author,index)=> (
                      <React.Fragment key={author.id || author.name}>
                        {index > 0 && " & "}
                        {author.id && author.id !== 5 ? <Link
                          to={`/author/${author.id}`}
                          data-testid="article-author-link"
                          className="font-semibold text-[#cf0a0a] hover:text-black transition-colors duration-300"
                        >{author.name}</Link> : <span className="font-semibold">{author.name}</span>}
                      </React.Fragment>
                    ))}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] leading-[1.35] text-[#888] dark:text-[#aaa] sm:hidden">
                      <time dateTime={story.dateIso} data-testid="article-published-mobile">{publicationDateLabel(story)}</time>
                      <span className="inline-flex items-center gap-1" data-testid="article-reading-time-mobile"><Clock size={11} /> {readingTime} min read</span>
                    </span>
                  </span>
                </span>
                <time dateTime={story.dateIso} data-testid="article-published-desktop" className="hidden sm:inline text-[#888] dark:text-[#aaa]">{publicationDateLabel(story)}</time>
                <span className="hidden sm:flex items-center gap-1.5 text-[#888] dark:text-[#aaa]" data-testid="article-reading-time">
                  <Clock size={12} /> {readingTime} min read
                </span>
                <div className="ml-auto shrink-0"><ShareRail story={story} bookmarkOnly /></div>
              </div>
              <div className="mt-4 lg:hidden"><ShareRail story={story} />
              </div>

              {story.img && (
                <figure className="mt-8">
                  <div className="img-frame overflow-hidden aspect-[16/9] bg-[var(--card-muted)]">
                    <img
                      src={story.img}
                      alt={titleCaseHeadline(story.imgAlt || story.title)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <figcaption className="mt-2.5 flex items-center gap-2 text-[11px] text-[var(--faint)]">
                    <span className="w-1.5 h-1.5 rotate-45 bg-[#cf0a0a] shrink-0" />
                    {titleCaseHeadline(story.imgAlt || story.title)}
                  </figcaption>
                </figure>
              )}

              <div
                data-testid="article-body"
                className="mt-9 max-w-[680px] space-y-6"
              >
                {bodyParts ? <><div className="wp-article-content" dangerouslySetInnerHTML={{__html:bodyParts[0]}} /><AdSlot placement="article-inline" /><div className="wp-article-content" dangerouslySetInnerHTML={{__html:bodyParts[1]}} /></> : paragraphs.map((p,i)=><div key={i}><p className="text-[17px] leading-8 text-[var(--muted)]">{p}</p>{i === 2 && <AdSlot placement="article-inline" />}</div>)}
              </div>

              <div
                className="mt-10 border-y border-[var(--line)] py-6 flex flex-wrap items-center justify-between gap-4"
                data-testid="applause-strip"
              >
                <div>
                  <p className="font-display text-lg font-semibold text-[var(--ink)]">
                    Enjoyed this story?
                  </p>
                  <p className="mt-0.5 text-[12px] text-[var(--muted)]">
                    Applaud it for the newsroom — one clap per reader, per story.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={applaud}
                  disabled={applauded}
                  aria-pressed={applauded}
                  data-testid="applause-button"
                  className={`flex items-center gap-2.5 border px-5 py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-all duration-300 ${
                    applauded
                      ? "border-[#cf0a0a] bg-[#cf0a0a] text-white"
                      : "border-[var(--line)] text-[var(--muted)] hover:border-[#cf0a0a] hover:text-[#cf0a0a] cursor-pointer"
                  }`}
                >
                  <Heart size={15} fill={applauded ? "currentColor" : "none"} />
                  {applause != null ? applause.toLocaleString() : "—"}
                  <span className="hidden sm:inline">{applauded ? "Applauded" : "Applaud"}</span>
                </button>
              </div>

              {(category || story.kicker) && (
                <div
                  className="mt-10 flex flex-wrap items-center gap-2"
                  data-testid="article-tags"
                >
                  <span className="mr-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--faint)]">
                    Filed under
                  </span>
                  {category && (
                    <Link
                      to={`/category/${category}`}
                      data-testid={`article-tag-${category}`}
                      className="border border-[var(--line)] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[var(--muted)] hover:border-[#cf0a0a] hover:text-[#cf0a0a] transition-colors duration-300"
                    >
                      {categoryLabel}
                    </Link>
                  )}
                  {story.kicker && story.kicker !== categoryLabel && (
                    <span className="border border-[var(--line)] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-[var(--muted)]">
                      {story.kicker}
                    </span>
                  )}
                </div>
              )}

              <p className="mt-10 pt-5 border-t border-[var(--line)] text-[11px] uppercase tracking-[0.18em] text-[var(--faint)]" data-testid="article-source-line">
                First published on standard.gm — {story.date}
              </p>
            </div>

            <div className="lg:col-span-3 min-w-0"><NewsSidebar excludeId={story.id} /></div>
          </div>
        </article>

        <section data-testid="article-other-news" className="border-t border-[var(--line)] bg-[var(--bg)]">
          <div className="mx-auto max-w-[1440px] px-4 sm:px-6 py-12 sm:py-16 grid gap-10 lg:grid-cols-[minmax(0,1.8fr)_minmax(0,1fr)] lg:gap-12">
            <div className="min-w-0">
              <h2 className="font-display text-2xl font-semibold mb-6">Other News</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-8">
                {otherNews.map(item => <Link key={item.id} to={`/${item.id}`} className="group min-w-0">
                  <div className="img-frame aspect-[16/9] overflow-hidden bg-[var(--card-muted)]"><img src={item.img || '/story-placeholder.svg'} alt={item.imgAlt || titleCaseHeadline(item.title)} loading="lazy" className="w-full h-full object-cover" /></div>
                  <h3 className="mt-3 font-display font-semibold text-[18px] leading-[1.25] line-clamp-3 group-hover:text-[var(--accent)] transition-colors">{titleCaseHeadline(item.title)}</h3>
                  <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--faint)]">{getArticle(item).readingTime} Min Read</p>
                </Link>)}
              </div>
            </div>
            <aside className="min-w-0 self-start" data-testid="article-top-stories">
              <h2 className="font-display text-2xl font-semibold mb-3">Top Stories</h2>
              <ol className="divide-y divide-[var(--line)]">
                {topStories.map((item, i) => <li key={item.id}><Link to={`/${item.id}`} className="group grid grid-cols-[38px_minmax(0,1fr)] gap-4 py-5">
                  <span className="font-display text-[32px] leading-none font-semibold">{i + 1}</span>
                  <h3 className="font-display text-[18px] leading-[1.3] line-clamp-2 group-hover:text-[var(--accent)] transition-colors">{titleCaseHeadline(item.title)}</h3>
                </Link></li>)}
              </ol>
            </aside>
          </div>
        </section>

        <NewsletterBand />
      </main>
      <Footer />
    </div>
  );
};

export default ArticlePage;
